import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Filter, ArrowRight, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";
import FinalCTA from "@/components/FinalCTA";
import BrochureCTA from "@/components/BrochureCTA";
import { GUEST_RANGES, EVENT_TYPES, CITIES } from "@/data/content";
import { trackEvent, EVENTS } from "@/utils/tracking";
import { getWhatsAppLink } from "@/utils/links";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VENUE_TYPE_ORDER = [
  "Luxury Hotels", "Premium Resorts", "Banquet Halls", "Farmhouses", "Open Lawns",
  "Heritage Venues", "Palace Venues", "Beach Venues", "Rooftop Venues", "Luxury Villas",
  "Convention Centres", "Corporate Event Venues",
];

export default function Venues() {
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event: "", io: "", venueType: "", city: "", guests: "", accommodation: "" });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/venues`);
        if (mounted) setVenues(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) setVenues([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Build venue type list from API data, falling back to canonical order.
  const venueTypes = useMemo(() => {
    const fromApi = Array.from(new Set(venues.map((v) => v.name).filter(Boolean)));
    if (fromApi.length === 0) return VENUE_TYPE_ORDER;
    return fromApi;
  }, [venues]);

  const filtered = useMemo(() => venues.filter((v) => {
    if (filters.io && v.io !== filters.io) return false;
    if (filters.venueType && v.name !== filters.venueType) return false;
    if (filters.city && v.city && !String(v.city).toLowerCase().includes(filters.city.toLowerCase())) return false;
    return true;
  }), [filters, venues]);

  const venueListLd = useMemo(() => {
    if (!venues.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      url: `${process.env.REACT_APP_SITE_URL || "https://petalspromises.com"}/venues`,
      itemListElement: venues.slice(0, 30).map((v, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: v.name,
        description: v.best_for || v.description || "",
      })),
    };
  }, [venues]);

  return (
    <div data-testid="page-venues" className="bg-[#FAF6F1]">
      <SEO
        title="Wedding & Event Venue Discovery — Hand-Picked Across India"
        description="Hand-picked luxury hotels, palace resorts, banquets, farmhouses, heritage venues, beach and rooftop venues across India — shortlisted to match your guest profile, city, and design vision."
        path="/venues"
        keywords="wedding venues India, luxury wedding venues, palace wedding venues, destination wedding venues, banquet halls, farmhouse wedding venues, heritage wedding venues, corporate event venues"
        jsonLd={venueListLd}
      />

      <section className="pt-28 md:pt-32 pb-10 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="pill">Venue Discovery</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[84px] leading-[1.0] mt-6 text-[#3B312D]">
              Hand-picked <span className="italic text-[#B65980]">venues</span> for every story.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[16.5px] text-[#3B312D]/80 leading-relaxed">
              Heritage palaces, beach resorts, ballrooms, rooftops, farmhouses, and convention centres —
              we shortlist venues that match your guest profile, city, and design vision.
            </p>
          </div>
        </div>
      </section>

      <section className="py-8 bg-[#F4DCD8]/35 border-y border-[rgba(59,49,45,0.06)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-[12px] tracking-[0.18em] uppercase text-[#3B312D] font-semibold">
            <Filter size={14} className="text-[#B65980]" /> Filter
          </div>
          <select className="lux-select max-w-xs" value={filters.event} onChange={(e) => setFilters({ ...filters, event: e.target.value })} data-testid="venue-filter-event">
            <option value="">Event type</option>
            {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
          <select className="lux-select max-w-xs" value={filters.io} onChange={(e) => setFilters({ ...filters, io: e.target.value })} data-testid="venue-filter-io">
            <option value="">Indoor / Outdoor</option>
            <option>Indoor</option><option>Outdoor</option><option>Indoor & Outdoor</option>
          </select>
          <select className="lux-select max-w-xs" value={filters.guests} onChange={(e) => setFilters({ ...filters, guests: e.target.value })} data-testid="venue-filter-guests">
            <option value="">Guest count</option>
            {GUEST_RANGES.map((g) => <option key={g}>{g}</option>)}
          </select>
          <select className="lux-select max-w-xs" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} data-testid="venue-filter-city">
            <option value="">City / Location</option>
            {CITIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select className="lux-select max-w-xs" value={filters.venueType} onChange={(e) => setFilters({ ...filters, venueType: e.target.value })} data-testid="venue-filter-venue-type">
            <option value="">Venue type</option>
            {venueTypes.map((v) => <option key={v}>{v}</option>)}
          </select>
          <select className="lux-select max-w-xs" value={filters.accommodation} onChange={(e) => setFilters({ ...filters, accommodation: e.target.value })} data-testid="venue-filter-accommodation">
            <option value="">Accommodation required</option>
            <option>Yes</option><option>No</option>
          </select>
          <button onClick={() => setFilters({ event: "", io: "", venueType: "", city: "", guests: "", accommodation: "" })} data-testid="venue-filter-reset" className="text-[12px] tracking-[0.18em] uppercase text-[#B65980] underline underline-offset-4 decoration-[#B65980] font-semibold">Reset</button>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#FAF6F1]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="venues-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="lux-card overflow-hidden h-[420px] animate-pulse">
                  <div className="h-60 bg-[#F4DCD8]/60" />
                  <div className="p-7 space-y-3">
                    <div className="h-5 w-2/3 bg-[#F4DCD8]/70 rounded" />
                    <div className="h-3 w-full bg-[#F4DCD8]/40 rounded" />
                    <div className="h-3 w-5/6 bg-[#F4DCD8]/40 rounded" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((v, i) => {
                const slug = (v.name || "venue").toLowerCase().replace(/\s+/g, "-");
                const description = v.best_for || v.description || "";
                return (
                  <Reveal key={v.id || v.name} delay={i * 60} className="lux-card overflow-hidden flex flex-col h-full" data-testid={`venue-card-${slug}`}>
                    <div className="relative h-60 image-zoom shrink-0">
                      <img src={v.image} alt={v.name} className="w-full h-full object-cover" />
                      {v.budget && <span className="absolute top-4 left-4 pill !bg-white/92 !text-[#3B312D] !border-white/0">{v.budget}</span>}
                    </div>
                    <div className="p-7 flex flex-col flex-1">
                      <h3 className="font-display text-2xl text-[#3B312D]">{v.name}</h3>
                      {description && <p className="mt-3 text-[14px] text-[#3B312D]/80 leading-relaxed">Best for: {description}</p>}
                      <div className="mt-5 grid grid-cols-2 gap-3 text-[12.5px] text-[#3B312D]">
                        <div><p className="text-[10px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold">Capacity</p>{v.capacity || "On request"}</div>
                        <div><p className="text-[10px] tracking-[0.22em] uppercase text-[#8A9A7B] font-semibold">Style</p>{v.io || "—"}</div>
                      </div>
                      <Link to="/contact" className="link-gold mt-6 inline-flex text-[12px] tracking-[0.18em] uppercase text-[#B65980] font-semibold" data-testid={`venue-cta-${slug}`} onClick={() => trackEvent(EVENTS.VENUE_INQUIRY_CLICK, { location: "venues-grid", content_name: v.name, content_category: v.category || "Venue" })}>
                        {v.cta_text || "Find Venues For My Event"} <ArrowRight size={14} />
                      </Link>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <p className="text-center text-[#3B312D]/70 py-20" data-testid="venues-empty">No venues match those filters. Try resetting.</p>
          )}
        </div>
      </section>

      {/* BEYOND THE LISTED VENUES */}
      <section className="py-20 md:py-24 bg-[#F4DCD8]/40 border-t border-[rgba(59,49,45,0.06)]" data-testid="venues-beyond-section">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
          <Sparkles className="mx-auto text-[#B65980]" size={28} />
          <p className="pill mx-auto mt-4">Beyond the listed venues</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-[56px] mt-5 text-[#3B312D] leading-[1.05]">
            Looking for something <span className="italic text-[#B65980]">truly bespoke?</span>
          </h2>
          <p className="mt-5 text-[16px] md:text-[17.5px] text-[#3B312D]/80 leading-relaxed max-w-2xl mx-auto">
            Our curated network extends far beyond what's listed here — from private estates and royal palaces to
            offbeat destinations across India and abroad. Tell us your vision, guest count, and dates, and we'll
            shortlist venues hand-picked for your celebration.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <Link to="/contact" className="btn-primary" data-testid="venues-beyond-cta-primary" onClick={() => trackEvent(EVENTS.VENUE_INQUIRY_CLICK, { location: "venues-beyond" })}>
              Request a custom shortlist <ArrowRight size={14} />
            </Link>
            <a href={getWhatsAppLink("Hey, Petals and Promises Team — I'd like a custom venue shortlist.")} target="_blank" rel="noreferrer noopener" className="btn-ghost" data-testid="venues-beyond-cta-whatsapp" onClick={() => trackEvent(EVENTS.WHATSAPP_CLICK, { location: "venues-beyond" })}>
              Chat with our venue team
            </a>
          </div>
        </div>
      </section>

      <BrochureCTA />
      <FinalCTA />
    </div>
  );
}
